import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PrinterIcon,
  CalendarIcon,
  UserIcon,
  ChartBarIcon,
  CashIcon,
  RefreshIcon,
  TrendingUpIcon,
  CurrencyDollarIcon,
  XIcon,
  DocumentReportIcon,
} from "@heroicons/react/outline";

import { useEffect, useState, useRef, useMemo } from "react";
import swal from "sweetalert";
import { useDispatch, useSelector } from "react-redux";
import LoadingBar from "react-top-loading-bar";
import ReactPaginate from "react-paginate";
import { format, subDays, isValid, parseISO } from "date-fns";
import { motion, AnimatePresence } from "framer-motion";
import { DateRangePicker } from "react-date-range";

import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

import { Bar } from "react-chartjs-2";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";

import { selectCurrentUser } from "../../Features/auth/authSlice";

import { useGetUserEmployeesQuery } from "../../Features/userEmployeesSlice";

import {
  useGetSalesSummaryQuery,
  useGetSalesSummaryWithDateRangeMutation,
  useGetSalesSummaryPageChangeMutation,
} from "../../Features/userReportsSlice";

import {
  selectDateRange,
  setDateRange,
} from "../../Features/date/dateSlice";

import { useTheme } from "../../ThemeContext";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend
);

const BRAND = "#80b643";
const BRAND_HOVER = "#719f3a";

const formatDate = (date) => {
  if (!date) return "";

  const parsedDate =
    date instanceof Date
      ? date
      : parseISO(String(date));

  return isValid(parsedDate)
    ? format(parsedDate, "yyyy-MM-dd")
    : "";
};

function SalesBySummaryView() {
  const { darkMode } = useTheme();

  const user = useSelector(selectCurrentUser);
  const date_range = useSelector(selectDateRange);

  const dispatch = useDispatch();
  const pickerRef = useRef(null);

  const [progress, setProgress] = useState(0);
  const [numRowsPerPage, setNumRowsPerPage] = useState(10);
  const [currentItems, setCurrentItems] = useState([]);
  const [stats, setStats] = useState([]);
  const [overalStats, setOveralStats] = useState({});
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [open, setOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState("0");
  const [selectedStartDate, setSelectedStartDate] = useState("");
  const [selectedEndDate, setSelectedEndDate] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);

  const [state, setState] = useState([
    {
      startDate: date_range?.startDate
        ? new Date(date_range.startDate)
        : subDays(new Date(), 7),
      endDate: date_range?.endDate
        ? new Date(date_range.endDate)
        : new Date(),
      key: "selection",
    },
  ]);

  const initialStartDate = date_range?.startDate
    ? formatDate(date_range.startDate)
    : format(subDays(new Date(), 7), "yyyy-MM-dd");

  const initialEndDate = date_range?.endDate
    ? formatDate(date_range.endDate)
    : format(new Date(), "yyyy-MM-dd");

  // =========================================================
  // API
  // =========================================================

  const {
    data: resp,
    isLoading,
    isSuccess,
  } = useGetSalesSummaryQuery(
    {
      user_id: user.id,
      pagination: "10",
      start_date: initialStartDate,
      end_date: initialEndDate,
      employee_id: "0",
    },
    {
      skip: !user?.id,
    }
  );

  const { data: employees } = useGetUserEmployeesQuery(
    {
      account_id: user.id,
      paginate: 10,
    },
    {
      skip: !user?.id,
    }
  );

  const [fetchCustomData] =
    useGetSalesSummaryWithDateRangeMutation();

  const [pageChange] =
    useGetSalesSummaryPageChangeMutation();

  // =========================================================
  // APPLY RESPONSE DATA
  // =========================================================

  const applyResponseData = (response) => {
    const summary = response?.data?.summary;
    const meta = summary?.meta;

    setCurrentItems(summary?.data || []);
    setStats(response?.data?.stats || []);
    setOveralStats(response?.data?.totals || {});

    setPageCount(
      meta?.per_page
        ? Math.ceil((meta.total || 0) / meta.per_page)
        : 0
    );
  };

  // =========================================================
  // INITIAL DATA
  // =========================================================

  useEffect(() => {
    setProgress(40);

    if (isLoading) {
      setProgress(80);
    }

    if (isSuccess && resp?.data) {
      applyResponseData(resp);
    }

    setProgress(100);
  }, [resp, isLoading, isSuccess]);

  // =========================================================
  // CLOSE DATE PICKER WHEN CLICKING OUTSIDE
  // =========================================================

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(event.target)
      ) {
        setOpen(false);
        setIsSelecting(false);
      }
    };

    document.addEventListener(
      "mousedown",
      handleClickOutside
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside
      );
    };
  }, []);

  // =========================================================
  // DATE RANGE
  // =========================================================

  const handleSelect = async (ranges) => {
    const selection = ranges.selection;

    setState([selection]);
    setCurrentPage(0);

    // Allow the user to select the start and end dates.
    if (!isSelecting) {
      setIsSelecting(true);
      return;
    }

    setIsSelecting(false);
    setOpen(false);

    const startDate = format(
      selection.startDate,
      "yyyy-MM-dd"
    );

    const endDate = format(
      selection.endDate,
      "yyyy-MM-dd"
    );

    setSelectedStartDate(startDate);
    setSelectedEndDate(endDate);

    dispatch(
      setDateRange({
        date_range: selection,
      })
    );

    try {
      setProgress(80);

      const response = await fetchCustomData({
        user_id: user.id,
        pagination: numRowsPerPage,
        start_date: startDate,
        end_date: endDate,
        employee_id: selectedEmployee || "0",
      }).unwrap();

      if (response?.success) {
        applyResponseData(response);
      } else {
        setCurrentItems([]);
        setStats([]);
        setOveralStats({});
        setPageCount(0);

        swal(
          "No results",
          "No sales summary records were found for the selected dates.",
          "info"
        );
      }
    } catch (err) {
      swal(
        "Error",
        "Unable to load sales summary for the selected dates.",
        "error"
      );
    } finally {
      setProgress(100);
    }
  };

  // =========================================================
  // EMPLOYEE FILTER
  // =========================================================

  const handleEmployeeChange = (event) => {
    const employeeId = event.target.value;

    setSelectedEmployee(employeeId);
    setCurrentPage(0);

    submitSearch(employeeId);
  };

  const submitSearch = async (employeeId = "0") => {
    setCurrentPage(0);

    const startDate = selectedStartDate || initialStartDate;
    const endDate = selectedEndDate || initialEndDate;

    try {
      setProgress(80);

      const response = await fetchCustomData({
        user_id: user.id,
        pagination: numRowsPerPage,
        start_date: startDate,
        end_date: endDate,
        employee_id: employeeId || "0",
      }).unwrap();

      if (response?.success && response?.data?.summary) {
        applyResponseData(response);
      } else {
        setCurrentItems([]);
        setStats([]);
        setOveralStats({});
        setPageCount(0);
      }
    } catch (err) {
      swal(
        "Error",
        "Unable to retrieve sales summary. Please try again.",
        "error"
      );
    } finally {
      setProgress(100);
    }
  };

  // =========================================================
  // ROWS PER PAGE
  // =========================================================

  const onChangePageRows = async (rows) => {
    const numRows = parseInt(rows, 10);

    setNumRowsPerPage(numRows);
    setCurrentPage(0);

    const startDate = selectedStartDate || initialStartDate;
    const endDate = selectedEndDate || initialEndDate;

    try {
      setProgress(80);

      const response = await pageChange({
        user_id: user.id,
        pagination: numRows,
        start_date: startDate,
        end_date: endDate,
        employee_id: selectedEmployee || "0",
        page: 1,
      }).unwrap();

      if (response?.success) {
        applyResponseData(response);
      }
    } catch (err) {
      swal(
        "Error",
        "Unable to load sales summary.",
        "error"
      );
    } finally {
      setProgress(100);
    }
  };

  // =========================================================
  // PAGE CHANGE
  // =========================================================

  const handlePageChange = async (selectedPage) => {
    setCurrentPage(selectedPage);

    const pageNum = selectedPage + 1;
    const startDate = selectedStartDate || initialStartDate;
    const endDate = selectedEndDate || initialEndDate;

    try {
      setProgress(80);

      const response = await pageChange({
        user_id: user.id,
        pagination: numRowsPerPage,
        start_date: startDate,
        end_date: endDate,
        employee_id: selectedEmployee || "0",
        page: pageNum,
      }).unwrap();

      if (response?.success) {
        applyResponseData(response);
      }
    } catch (err) {
      swal(
        "Error",
        "An unexpected error occurred. Please try again.",
        "error"
      );
    } finally {
      setProgress(100);
    }
  };

  // =========================================================
  // EXPORT CSV
  // =========================================================

  const handleExportCSV = async () => {
    try {
      setIsExporting(true);

      const employeeId = selectedEmployee || "0";
      const startDate = selectedStartDate || initialStartDate;
      const endDate = selectedEndDate || initialEndDate;

      // Uses the configured API URL when available.
      const configuredApiUrl =
        process.env.REACT_APP_API_URL ||
        "https://demoapi.autozedltd.com";

      const apiUrl = configuredApiUrl.replace(/\/$/, "");

      const response = await fetch(
        `${apiUrl}/user/sales-summary/${user.id}/100000/${startDate}/${endDate}/${employeeId}`
      );

      if (!response.ok) {
        throw new Error("Failed to retrieve export data.");
      }

      const result = await response.json();

      if (
        !result.success ||
        !result.data?.summary?.data?.length
      ) {
        swal(
          "No data",
          "No sales summary records were found for export.",
          "info"
        );

        return;
      }

      const summary = result.data.summary.data;

      const headers = [
        "Date / Time",
        "Gross Sales (K)",
        "Discounts (K)",
        "Refunds (K)",
        "Net Sales (K)",
        "Cost of Goods (K)",
        "Gross Profit (K)",
        "Margin (%)",
        "Taxes (K)",
      ];

      const rows = summary.map((row) => [
        row.label,
        row.gross_sales,
        row.discounts,
        row.refunds,
        row.net_sales,
        row.cost_of_goods,
        row.gross_profit,
        row.margin,
        row.taxes,
      ]);

      const csvContent = [headers, ...rows]
        .map((row) =>
          row
            .map(
              (value) =>
                `"${String(value ?? "").replace(/"/g, '""')}"`
            )
            .join(",")
        )
        .join("\r\n");

      const blob = new Blob(
        ["\uFEFF", csvContent],
        {
          type: "text/csv;charset=utf-8;",
        }
      );

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = `sales_summary_${startDate}_to_${endDate}.csv`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Sales summary export failed:", error);

      swal(
        "Error",
        "Failed to export sales summary. Please try again.",
        "error"
      );
    } finally {
      setIsExporting(false);
    }
  };

  // =========================================================
  // FORMAT MONEY
  // =========================================================

  const formatMoney = (value) => {
    return Number(value ?? 0).toLocaleString("en-ZM", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatReportDate = (value) => {
    if (!value) return "—";

    const parsedDate =
      value instanceof Date
        ? value
        : parseISO(String(value));

    return isValid(parsedDate)
      ? format(parsedDate, "MMM dd, yyyy")
      : String(value);
  };

  // =========================================================
  // CHART DATA
  // =========================================================

  const chartData = useMemo(() => {
    if (!Array.isArray(stats) || stats.length === 0) {
      return null;
    }

    const labels = stats.map((item) => {
      if (!item.label) return "";

      if (String(item.label).includes(":")) {
        return String(item.label).split(" ")[1] || item.label;
      }

      const parsedDate = parseISO(String(item.label));

      return isValid(parsedDate)
        ? format(parsedDate, "MMM dd")
        : item.label;
    });

    return {
      labels,
      datasets: [
        {
          label: "Gross Sales",
          data: stats.map((item) =>
            Number(item.gross_sales ?? 0)
          ),
          backgroundColor: BRAND,
          hoverBackgroundColor: BRAND_HOVER,
          borderRadius: 2,
          borderSkipped: false,
          maxBarThickness: 45,
        },
      ],
    };
  }, [stats]);

  // =========================================================
  // CHART OPTIONS
  // =========================================================

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,

      plugins: {
        legend: {
          display: false,
        },

      datalabels: {
        display: false
      },

        tooltip: {
          backgroundColor: darkMode ? "#111827" : "#ffffff",
          titleColor: darkMode ? "#ffffff" : "#111827",
          bodyColor: darkMode ? "#e5e7eb" : "#4b5563",
          borderColor: darkMode ? "#374151" : "#e5e7eb",
          borderWidth: 1,
          padding: 12,

          callbacks: {
            label: (context) =>
              `Gross Sales: K ${Number(
                context.raw ?? 0
              ).toLocaleString("en-ZM", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}`,
          },
        },
      },

      scales: {
        x: {
          ticks: {
            color: darkMode ? "#9ca3af" : "#6b7280",
            font: {
              size: 11,
            },
          },

          grid: {
            display: false,
          },

          border: {
            display: false,
          },
        },

        y: {
          beginAtZero: true,

          ticks: {
            color: darkMode ? "#9ca3af" : "#6b7280",
            font: {
              size: 11,
            },

            callback: (value) => `K ${value}`,
          },

          grid: {
            color: darkMode ? "#273244" : "#f0f2f5",
          },

          border: {
            display: false,
          },
        },
      },
    }),
    [darkMode]
  );

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <div className="w-full min-h-screen bg-gray-100 dark:bg-gray-950 text-gray-800 dark:text-gray-100 pb-12">

      <LoadingBar
        color={BRAND}
        height={4}
        progress={progress}
        onLoaderFinished={() => setProgress(0)}
      />

      {/* ===================================================== */}
      {/* PAGE HEADER */}
      {/* ===================================================== */}

      <div className="px-4 sm:px-6 lg:px-8 pt-6">

        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-5">

          <div className="flex items-center gap-3">

            <div className="w-11 h-11 shrink-0 rounded-xl bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">
              <ChartBarIcon className="h-6 w-6 text-[#80b643]" />
            </div>

            <div>
              <h1 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                Sales Summary
              </h1>

              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Monitor sales performance and financial activity.
              </p>
            </div>

          </div>

          {/* FILTERS */}

          <div className="flex flex-col sm:flex-row gap-3">

            {/* DATE RANGE PICKER */}

            <div className="relative" ref={pickerRef}>

              <button
                type="button"
                onClick={() => {
                  setOpen((previous) => !previous);
                  setIsSelecting(false);
                }}
                className="w-full sm:w-auto flex items-center justify-between gap-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 px-4 py-2.5 rounded-xl text-sm text-gray-700 dark:text-gray-200 shadow-sm hover:border-[#80b643] transition focus:outline-none focus:ring-2 focus:ring-[#80b643]/20"
              >
                <span className="flex items-center gap-2">
                  <CalendarIcon className="h-4 w-4 text-[#80b643] shrink-0" />

                  <span className="whitespace-nowrap">
                    {format(
                      date_range?.startDate
                        ? new Date(date_range.startDate)
                        : state[0].startDate,
                      "MMM dd, yyyy"
                    )}

                    {" – "}

                    {format(
                      date_range?.endDate
                        ? new Date(date_range.endDate)
                        : state[0].endDate,
                      "MMM dd, yyyy"
                    )}
                  </span>
                </span>

                <ChevronDownIcon
                  className={`h-4 w-4 text-gray-400 transition-transform ${
                    open ? "rotate-180" : ""
                  }`}
                />
              </button>

              <AnimatePresence>
                {open && (
                  <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.18 }}
                    className="absolute left-0 sm:left-auto sm:right-0 z-50 mt-2 w-[calc(100vw-2rem)] sm:w-auto max-w-[calc(100vw-2rem)] bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl overflow-x-auto"
                  >
                    <div className="flex items-center justify-between gap-3 p-3 border-b border-gray-100 dark:border-gray-800">
                      <div>
                        <p className="text-sm font-semibold text-gray-800 dark:text-white">
                          Select date range
                        </p>

                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {isSelecting
                            ? "Now select the end date."
                            : "Select a start date, then an end date."}
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          setOpen(false);
                          setIsSelecting(false);
                        }}
                        className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400"
                        aria-label="Close date picker"
                      >
                        <XIcon className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="max-w-full overflow-x-auto">
                      <DateRangePicker
                        onChange={handleSelect}
                        showSelectionPreview
                        moveRangeOnFirstSelection={false}
                        months={1}
                        ranges={state}
                        direction="horizontal"
                        rangeColors={[BRAND]}
                        maxDate={new Date()}
                      />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

            </div>

            {/* EMPLOYEE FILTER */}

            <div className="relative">

              <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#80b643] pointer-events-none" />

              <select
                value={selectedEmployee}
                onChange={handleEmployeeChange}
                className="w-full sm:w-auto min-w-[180px] appearance-none bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 pl-9 pr-10 py-2.5 rounded-xl text-sm text-gray-700 dark:text-gray-200 shadow-sm hover:border-[#80b643] focus:outline-none focus:ring-2 focus:ring-[#80b643]/20 focus:border-[#80b643] transition cursor-pointer"
              >
                <option value="0">All Employees</option>

                {(
                  employees?.data?.data ||
                  employees?.data ||
                  []
                ).map((item) => (
                  <option key={item.id} value={String(item.id)}>
                    {item.fname} {item.lname}
                  </option>
                ))}
              </select>

              <ChevronDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />

            </div>

          </div>

        </div>

      </div>

      {/* ===================================================== */}
      {/* STAT CARDS */}
      {/* ===================================================== */}

      <div className="px-4 sm:px-6 lg:px-8 mt-6">

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">

          <StatCard
            title="Gross Sales"
            value={`K ${formatMoney(overalStats.gross_sales)}`}
            icon={<ChartBarIcon className="h-5 w-5" />}
          />

          <StatCard
            title="Discounts"
            value={`K ${formatMoney(overalStats.discounts)}`}
            icon={<CashIcon className="h-5 w-5" />}
          />

          <StatCard
            title="Refunds"
            value={`K ${formatMoney(overalStats.refunds)}`}
            icon={<RefreshIcon className="h-5 w-5" />}
          />

          <StatCard
            title="Net Sales"
            value={`K ${formatMoney(overalStats.net_sales)}`}
            icon={<TrendingUpIcon className="h-5 w-5" />}y
          />

          <StatCard
            title="Gross Profit"
            value={`K ${formatMoney(overalStats.gross_profit)}`}
            icon={
              <div
                className="
                  flex items-center justify-center
                  w-10 h-10
                  rounded-xl
                  bg-amber-500
                  text-white
                  font-bold
                  text-xs
                "
              >
                ZMW
              </div>
            }
          />

        </div>

      </div>

      {/* ===================================================== */}
      {/* CHART */}
      {/* ===================================================== */}

      <div className="px-4 sm:px-6 lg:px-8 mt-6">

        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm overflow-hidden">

          <div className="px-5 sm:px-6 py-5 border-b border-gray-100 dark:border-gray-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">

            <div>
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                Sales Performance
              </h2>

              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Gross sales activity for the selected period.
              </p>
            </div>

            <div className="inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <span className="w-2.5 h-2.5 rounded-full bg-[#80b643]" />
              Gross Sales
            </div>

          </div>

          <div className="relative h-[280px] sm:h-[350px] p-4 sm:p-6">

            {isLoading ? (
              <div className="h-full flex flex-col items-center justify-center gap-3 text-gray-400">
                <div className="w-8 h-8 border-2 border-gray-200 border-t-[#80b643] rounded-full animate-spin" />
                <p className="text-sm">Loading sales data...</p>
              </div>
            ) : chartData ? (
              <Bar data={chartData} options={chartOptions} />
            ) : (
              <EmptyState
                icon={<ChartBarIcon className="h-6 w-6" />}
                title="No chart data available"
                description="Sales activity will appear here when records are available."
              />
            )}

          </div>

        </div>

      </div>

      {/* ===================================================== */}
      {/* SALES TABLE */}
      {/* ===================================================== */}

      <div className="px-4 sm:px-6 lg:px-8 mt-6">

        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm overflow-hidden">

          {/* TABLE HEADER */}

          <div className="px-5 sm:px-6 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-gray-100 dark:border-gray-800">

            <div className="flex items-center gap-3">

              <div className="w-10 h-10 rounded-lg bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">
                <DocumentReportIcon className="h-5 w-5 text-[#80b643]" />
              </div>

              <div>
                <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                  Sales Details
                </h2>

                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Detailed sales and financial breakdown.
                </p>
              </div>

            </div>

            <button
              type="button"
              onClick={handleExportCSV}
              disabled={isExporting || currentItems.length === 0}
              className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition ${
                isExporting || currentItems.length === 0
                  ? "bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                  : "bg-[#80b643] hover:bg-[#719f3a] text-white shadow-sm"
              }`}
            >
              {isExporting ? (
                <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              ) : (
                <PrinterIcon className="h-4 w-4" />
              )}

              {isExporting ? "Exporting..." : "Export CSV"}
            </button>

          </div>

          {/* TABLE CONTENT */}

          {isLoading && currentItems.length === 0 ? (
            <div className="py-16 flex flex-col items-center justify-center gap-3">
              <div className="w-8 h-8 border-2 border-gray-200 dark:border-gray-700 border-t-[#80b643] rounded-full animate-spin" />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Loading sales summary...
              </p>
            </div>
          ) : currentItems.length > 0 ? (
            <>
              <div className="overflow-x-auto">

                <table className="min-w-[1100px] w-full">

                  <thead className="bg-gray-50 dark:bg-gray-800/70 border-b border-gray-200 dark:border-gray-800">

                    <tr>
                      <TableHeader>Date / Time</TableHeader>
                      <TableHeader>Gross Sales</TableHeader>
                      <TableHeader>Discounts</TableHeader>
                      <TableHeader>Refunds</TableHeader>
                      <TableHeader>Net Sales</TableHeader>
                      <TableHeader>Cost of Goods</TableHeader>
                      <TableHeader>Gross Profit</TableHeader>
                      <TableHeader>Margin</TableHeader>
                      <TableHeader>Taxes</TableHeader>
                    </tr>

                  </thead>

                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">

                    {currentItems.map((row, index) => (
                      <tr
                        key={`${row.label || "sales"}-${index}`}
                        className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                      >
                        <TableCell bold>
                          {formatReportDate(row.label)}
                        </TableCell>

                        <TableCell>
                          K {formatMoney(row.gross_sales)}
                        </TableCell>

                        <TableCell>
                          K {formatMoney(row.discounts)}
                        </TableCell>

                        <TableCell>
                          K {formatMoney(row.refunds)}
                        </TableCell>

                        <TableCell>
                          K {formatMoney(row.net_sales)}
                        </TableCell>

                        <TableCell>
                          K {formatMoney(row.cost_of_goods)}
                        </TableCell>

                        <TableCell>
                          K {formatMoney(row.gross_profit)}
                        </TableCell>

                        <TableCell>
                          <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-[#80b643]/10 text-[#719f3a] dark:text-[#a3d76c] text-xs font-semibold">
                            {formatMoney(row.margin)}%
                          </span>
                        </TableCell>

                        <TableCell>
                          K {formatMoney(row.taxes)}
                        </TableCell>
                      </tr>
                    ))}

                  </tbody>

                </table>

              </div>

              {/* PAGINATION */}

              <div className="px-5 sm:px-6 py-4 border-t border-gray-100 dark:border-gray-800 flex flex-col lg:flex-row items-center justify-between gap-4">

                <div className="text-sm text-gray-500 dark:text-gray-400 text-center lg:text-left">
                  Showing{" "}
                  <span className="font-medium text-gray-800 dark:text-gray-200">
                    {currentItems.length}
                  </span>{" "}
                  records on this page
                </div>

                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">

                  <ReactPaginate
                    breakLabel="..."
                    nextLabel={
                      <ChevronRightIcon className="h-4 w-4" />
                    }
                    previousLabel={
                      <ChevronLeftIcon className="h-4 w-4" />
                    }
                    onPageChange={(event) =>
                      handlePageChange(event.selected)
                    }
                    pageRangeDisplayed={3}
                    marginPagesDisplayed={1}
                    pageCount={pageCount}
                    forcePage={Math.min(
                      currentPage,
                      Math.max(0, pageCount - 1)
                    )}
                    renderOnZeroPageCount={null}
                    containerClassName="flex flex-wrap items-center justify-center gap-1"
                    pageClassName="flex"
                    pageLinkClassName="flex items-center justify-center min-w-[32px] h-8 px-2 rounded-lg text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-[#80b643] transition cursor-pointer"
                    previousClassName="flex"
                    previousLinkClassName="flex items-center justify-center w-8 h-8 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-[#80b643] transition cursor-pointer"
                    nextClassName="flex"
                    nextLinkClassName="flex items-center justify-center w-8 h-8 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-[#80b643] transition cursor-pointer"
                    breakClassName="flex"
                    breakLinkClassName="flex items-center justify-center min-w-[32px] h-8 px-2 rounded-lg text-sm text-gray-400 cursor-default"
                    activeLinkClassName="!bg-[#80b643] !text-white"
                    disabledLinkClassName="!text-gray-300 dark:!text-gray-600 !cursor-not-allowed"
                  />

                  <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">

                    <label htmlFor="rows-per-page">
                      Rows per page
                    </label>

                    <select
                      id="rows-per-page"
                      value={numRowsPerPage}
                      onChange={(event) =>
                        onChangePageRows(event.target.value)
                      }
                      className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-[#80b643]/20 focus:border-[#80b643]"
                    >
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                    </select>

                  </div>

                </div>

              </div>
            </>
          ) : (
            <EmptyState
              icon={<DocumentReportIcon className="h-7 w-7" />}
              title="No sales records found"
              description="Try selecting another date range or employee to view sales records."
            />
          )}

        </div>

      </div>

    </div>
  );
}

// =========================================================
// STAT CARD
// =========================================================

function StatCard({
  title,
  value,
  icon,
  highlight = false,
}) {
  return (
    <div className={`bg-white dark:bg-gray-900 border rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow ${
      highlight
        ? "border-[#80b643]/40"
        : "border-gray-200 dark:border-gray-800"
    }`}>

      <div className="flex items-center justify-between gap-3">

        <div className="min-w-0">

          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
            {title}
          </p>

          <p className={`mt-2 text-lg sm:text-xl font-semibold break-words ${
            highlight
              ? "text-[#80b643]"
              : "text-gray-900 dark:text-white"
          }`}>
            {value}
          </p>

        </div>

        <div className="shrink-0 w-10 h-10 rounded-xl bg-[#80b643]/10 dark:bg-[#80b643]/20 text-[#80b643] flex items-center justify-center">
          {icon}
        </div>

      </div>

    </div>
  );
}

// =========================================================
// TABLE HEADER
// =========================================================

function TableHeader({ children }) {
  return (
    <th className="px-5 py-4 text-left text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
      {children}
    </th>
  );
}

// =========================================================
// TABLE CELL
// =========================================================

function TableCell({
  children,
  bold = false,
  highlight = false,
}) {
  return (
    <td className={`px-5 py-4 whitespace-nowrap text-sm ${
      highlight
        ? "font-semibold text-[#80b643]"
        : bold
          ? "font-medium text-gray-900 dark:text-white"
          : "text-gray-600 dark:text-gray-300"
    }`}>
      {children}
    </td>
  );
}

// =========================================================
// EMPTY STATE
// =========================================================

function EmptyState({
  icon,
  title,
  description,
}) {
  return (
    <div className="min-h-[220px] flex flex-col items-center justify-center text-center px-5 py-12">

      <div className="w-14 h-14 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-gray-400 dark:text-gray-500">
        {icon}
      </div>

      <h3 className="mt-4 text-sm font-semibold text-gray-800 dark:text-gray-200">
        {title}
      </h3>

      <p className="mt-2 max-w-md text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
        {description}
      </p>

    </div>
  );
}

export default SalesBySummaryView;